<template>
  <div class="cpe-credits-external-modal">
    <div class="cpe-credits-external-modal__title">
      Upload your non-INE training documentation
    </div>
    <div class="cpe-credits-external-modal__description">
      Non-INE training completed may be eligible for CPE credits. Upload your
      supporting documentation, and our team will assess its eligibility.
    </div>
    <div class="cpe-credits-external-modal__form">
      <div class="cpe-credits-external-modal__form-group">
        <label for="file" class="cpe-credits-external-modal__form-label"
          >Certification to renew
        </label>
        <Dropdown
          class="cpe-credits-external-modal__form-dropdown-certification"
          :options="certificationsToRenew"
          :handleSelection="certificationsToRenewDropdownHandler"
          :placeholder="'Select certification to renew'"
        />
      </div>
      <div class="cpe-credits-external-modal__form-group">
        <label for="file" class="cpe-credits-external-modal__form-label"
          >Training type
        </label>
        <Dropdown
          class="cpe-credits-external-modal__form-dropdown-training-type"
          :options="trainingTypes"
          :handleSelection="trainingTypeDropdownHandler"
          :placeholder="'Select training type'"
        />
        <div
          v-if="
            selectedTrainingType &&
              selectedTrainingType.code === 'certification'
          "
          class="cpe-credits-external-modal__form-dropdown-training-type-info"
        >
          To determine if your certification or related training is ISO-17024
          accredited, consult the
          <a :href="anabLink" target="_blank">ANAB Accreditation list</a>.
        </div>
        <div
          v-if="selectedTrainingType && selectedTrainingType.code === 'course'"
          class="cpe-credits-external-modal__form-dropdown-training-type-info"
        >
          <div class="cpe-credits-external-modal__form-info">
            Master’s, Doctorate, or PhD-level courses from accredited
            institutions.
          </div>
          <div class="cpe-credits-external-modal__form-info">
            United States institutions:
            <ul>
              <li class="cpe-credits-external-modal__form-info-item">
                Accredited institutions in the United States can be found
                <a :href="dapipLink" target="_blank">here</a>.
              </li>
            </ul>
          </div>
          <div class="cpe-credits-external-modal__form-info">
            International institutions:
            <ul>
              <li class="cpe-credits-external-modal__form-info-item">
                Credential Validation: We use WES to validate international
                credentials to US standards for INE CPE eligibility.
              </li>
              <li class="cpe-credits-external-modal__form-info-item">
                Start the Evaluation: Visit
                <a :href="wesLink" target="_blank">WES</a>
                to learn more. Choose the Course-by-Course evaluation for INE
                CPE.
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="
            selectedTrainingType && selectedTrainingType.code === 'training'
          "
          class="cpe-credits-external-modal__form-dropdown-training-type-info"
        >
          DOD or Military Training, Skill-based Training Courses, Vendor
          Training, All-day or Multi-day Conferences
        </div>
      </div>
      <div class="cpe-credits-external-modal__form-group">
        <DragAndDropFileUploader
          @files-uploaded="filesUploadedHandler"
          @files-uploaded-validity="filesUploadedValidityHandler"
        />
        <div class="cpe-credits-external-modal__form-info">
          Documentation should be in the form of an official certificate,
          attendance confirmation, training report, digital badge link and must
          include:
          <ul>
            <li class="cpe-credits-external-modal__form-info-item">
              Earner's name
            </li>
            <li class="cpe-credits-external-modal__form-info-item">
              Name of training provider or certification body
            </li>
            <li class="cpe-credits-external-modal__form-info-item">
              Name of training course or certification
            </li>
            <li class="cpe-credits-external-modal__form-info-item">
              Dates of training or total training hours
            </li>
            <li class="cpe-credits-external-modal__form-info-item">
              Date certification was earned
            </li>
          </ul>
        </div>
      </div>
      <div class="cpe-credits-external-modal__form-group">
        <div class="cpe-credits-external-modal__btn">
          <ActionLoader v-if="isSubmiting" name="ball-clip-rotate" />
          <button
            v-else
            :disabled="!isSubmitButtonEnabled"
            class="btn btn--primary btn--small btn--full-mobile"
            @click="submitDocumentation"
          >
            Submit for review
          </button>
        </div>
      </div>
    </div>
    <div class="cpe-credits-external-modal__learn-more">
      <a :href="linkToCPEProgram" target="_blank">
        Learn more
      </a>
      about certification renewals program and what qualifies as CPE credits.
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import DragAndDropFileUploader from '@/components/shared/DragAndDropFileUploader.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'

const {
  CPE: { LINK_TO_CPE_PROGRAM },
  CERTIFICATIONS: {
    RENEWAL_STATUS: { EXPIRED },
  },
} = CONSTANTS

export default {
  name: 'CPECreditsExternalModal',
  components: {
    Dropdown,
    DragAndDropFileUploader,
    ActionLoader,
  },
  data() {
    return {
      trainingTypes: [
        { code: 'certification', label: 'Accredited certification' },
        { code: 'course', label: 'Graduated Courses' },
        { code: 'training', label: 'Professional Training' },
      ],
      selectedCertification: null,
      selectedTrainingType: null,
      filesUploaded: false,
      files: [],
      anabLink:
        'https://anabpd.ansi.org/Accreditation/credentialing/personnel-certification/ALLdirectoryListing?menuID=2&prgID=201&statusID=4',
      dapipLink: 'https://ope.ed.gov/dapip/',
      wesLink: 'https://www.wes.org/credential-evaluations/',
      isSubmiting: false,
      linkToCPEProgram: LINK_TO_CPE_PROGRAM,
    }
  },
  computed: {
    ...mapGetters('certifications', ['usersCertifications']),
    certificationsToRenew() {
      return this.usersCertifications
        .filter(certs => !(certs.expiration_status === EXPIRED))
        .map(cert => ({
          code: cert.attempt_id,
          label: cert.name,
        }))
    },
    isSubmitButtonEnabled() {
      return (
        this.selectedCertification &&
        this.selectedTrainingType &&
        this.filesUploaded
      )
    },
  },
  methods: {
    ...mapActions('cpeProgram', ['postCPEExternalDocumentation']),
    ...mapActions('app', ['setError']),
    certificationsToRenewDropdownHandler(selectedCertification) {
      this.selectedCertification = selectedCertification
    },
    trainingTypeDropdownHandler(selectedTrainingType) {
      this.selectedTrainingType = selectedTrainingType
    },
    async submitDocumentation() {
      try {
        if (this.isSubmitButtonEnabled) {
          const formData = new FormData()
          formData.append('source_id', this.selectedCertification.code)
          formData.append('training_type', this.selectedTrainingType.code)
          this.files.forEach(file => {
            formData.append('files', file)
          })

          this.isSubmiting = true
          const uploadResult = await this.postCPEExternalDocumentation(formData)
          if (uploadResult.success) {
            this.isSubmiting = false
            this.$emit('close-modal')
          }
        }
      } catch (error) {
        this.isSubmiting = false
        this.setError(error)
      }
    },
    filesUploadedHandler(files) {
      this.files = files
    },
    filesUploadedValidityHandler(filesUploaded) {
      this.filesUploaded = filesUploaded
    },
  },
}
</script>

<style lang="scss" scoped>
.cpe-credits-external-modal {
  max-width: 650px;
  display: flex;
  flex-flow: column;
  gap: 20px;
  &__title {
    color: var(--white);
    font-size: rem(40px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(52px);
  }

  &__description {
    color: var(--light-blue-gray);
    font-size: rem(18px);
    font-style: normal;
    font-weight: 500;
    line-height: rem(24px);
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 15px;

    &-group {
      display: flex;
      flex-direction: column;
      text-align: left;
    }

    &-label {
      color: var(--menu-subcategory);
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
    }

    &-dropdown {
      &-certification,
      &-training-type {
        margin: 6px 0;
      }
      &-training-type-info {
        color: var(--light-blue-gray);
        font-size: rem(12px);
        font-style: normal;
        font-weight: 450;
        line-height: rem(14px);
        a {
          color: var(--purple-pale);
          text-decoration: none;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    &-info {
      margin-top: 6px;
      color: var(--light-blue-gray);
      font-size: rem(12px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(14px);
      &-item {
        a {
          color: var(--purple-pale);
          text-decoration: none;
          &:hover {
            text-decoration: underline;
          }
        }
      }
      ul {
        padding-left: 20px;
      }
    }
  }
  &__learn-more {
    color: var(--light-blue-gray);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(20px);
    a {
      color: var(--purple-pale);
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &__btn {
    width: 100%;
    display: flex;
    justify-content: center;
    button {
      width: 130px;
    }
  }
}
</style>
